import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  LinearProgress,
  Snackbar,
  Alert,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Chip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Upload as UploadIcon,
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  SystemUpdateAlt as ExcelIcon,
  Report as ReportIcon,
} from "@mui/icons-material";
import axiosInstance from "../axiosInstance";
import configData from "../config";

export default function ComplianceAgent() {
  // Separate file states
  const [docFile, setDocFile] = useState(null);           // main document (pdf/xls/xlsx/csv)
  const [instrFile, setInstrFile] = useState(null);       // instructions excel (xls/xlsx)

  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [error, setError] = useState("");
  const [aiResponse, setAiResponse] = useState('');
  const [downloadFilename, setDownloadFilename] = useState("");

  // Refs for two file inputs
  const docInputRef = useRef(null);
  const instrInputRef = useRef(null);

  const [excelOpen, setExcelOpen] = useState(false);
  const [iframeKey, setIframeKey] = useState(Date.now());

  // If you source this elsewhere, replace here:
  const complianceFileId = "ComplianceAgent_21.xlsx";
  const COMPLIANCE_EXCEL_URL =
    "https://projectivetech-my.sharepoint.com/:x:/p/resource1/ET23qOWgeSdEpjLS1cibJhoBcEwpzE_iC8xF9odzPeI0sA?e=gfhdGn";

  const baseSharepointUrl =
    COMPLIANCE_EXCEL_URL +
    "&action=embedview" +
    "&AllowTyping=True" +
    "&wdDownloadButton=True" +
    "&wdInConfigurator=True" +
    "&wdHideHeaders=True";

  const DOCUMENT_ALLOWED = new Set(["pdf", "doc", "docx", "xls", "xlsx", "csv", "txt"]);
  const INSTR_ALLOWED = new Set(["xls", "xlsx"]);

  const onDocChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = f.name.split(".").pop()?.toLowerCase();
    if (!ext || !DOCUMENT_ALLOWED.has(ext)) {
      setError("Please upload a valid Document file (pdf/xls/xlsx/csv/doc/docx/txt).");
      setSnackbarOpen(true);
    } else {
      setDocFile(f);
    }
    e.target.value = null;
  };

  const onInstrChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = f.name.split(".").pop()?.toLowerCase();
    if (!ext || !INSTR_ALLOWED.has(ext)) {
      setError("Please upload the Compliance Instruction in Excel (xls/xlsx).");
      setSnackbarOpen(true);
    } else {
      setInstrFile(f);
    }
    e.target.value = null;
  };

  const clearDoc = () => setDocFile(null);
  const clearInstr = () => setInstrFile(null);

  const handleSubmit = async () => {
    if (!docFile || !instrFile) {
      setError("Please upload both files before submitting.");
      setSnackbarOpen(true);
      return;
    }
    setLoading(true);
    setStatusMessage("📦 Uploading files…");
    setUploadProgress(0);

    try {
      // === REAL API CALL ===
      const formData = new FormData();
      // Use clear field names so backend can read both files
      formData.append("document", docFile);
      formData.append("compliance_excel", instrFile);
      formData.append("complianceFileId", complianceFileId);

      const token = localStorage.getItem("token");
      const res = await axiosInstance.post(
        //`https://api.projectivetech.com/user/validate_compliance_agent`,
        `http://localhost:8000/document_agent/validate_compliance_agent`,  
        formData,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token || ""}`,
          },
          body: formData,
        }
      );

      const { results } = res.data;
      setAiResponse(results || []);
      
      const filename = res.data.pdf_filename || "compliance_report.pdf";
      setDownloadFilename(filename);
      setStatusMessage("✅ Analysis ready");
    } catch (e) {
      console.error(e);
      const apiErr =
        e?.response?.data?.error ||
        e?.message ||
        "Upload/analysis failed. Please try again.";
      setError(apiErr);
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!downloadFilename) {
      setError("PDF not available yet.");
      setSnackbarOpen(true);
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        //`https://api.projectivetech.com/user/download_analysis_pdf?filename=${encodeURIComponent(downloadFilename)}`,
        `http://localhost:8000/document_agent/download_analysis_pdf?filename=${encodeURIComponent(downloadFilename)}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token || ""}`
          }
        }
      );

      if (!res.ok) throw new Error("File not found or download failed.");

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", downloadFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
      setError("Download failed. Please try again.");
      setSnackbarOpen(true);
    }
  };

  const handleUpdateExcel = async () => {
    if (!aiResponse) {
      setError("Run analysis first before updating Excel.");
      setSnackbarOpen(true);
      return;
    }
    if (!baseSharepointUrl) {
      setError("Excel link is not configured for your account.");
      setSnackbarOpen(true);
      return;
    }
    try {
      const formData = new FormData();
      formData.append("compliance_excel", instrFile);  // file from <input type="file" />
      formData.append("compliance_file_id", complianceFileId);
      formData.append("ai_results", JSON.stringify(aiResponse));  // should include Analysis + Red Flags
      const token = localStorage.getItem("token");
      await fetch(`http://localhost:8000/document_agent/upload_ai_results_to_excel`, {
      //await fetch(`http://https://api.projectivetech.com/user/upload_ai_results_to_excel`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${token || ""}`,
        },
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            alert("✅ Excel updated and uploaded to OneDrive.");
          } else {
            alert("❌ " + data.message);
          }
        });
      setStatusMessage("✅ Excel updated.");
    } catch (err) {
      console.error("Excel update failed:", err);
      const apiErr =
        err?.response?.data?.error ||
        err?.message ||
        "Excel update failed. Please try again.";
      setError(apiErr);
      setSnackbarOpen(true);
    } finally {
      // open the embedded excel regardless of update success
      setExcelOpen(true);
      setIframeKey(Date.now());
    }
  };
  

  const canSubmit = !!docFile && !!instrFile && !loading;

  return (
    <Box
      display="flex"
      height="85vh"
      sx={{ gap: 2, p: { xs: 1, md: 2 }, bgcolor: "#f6f7fb" }}
    >
      {/* Left Panel: AI Compliance Analysis */}
      <Box flex={3} minWidth={0}>
        <Card elevation={1} sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <CardHeader
            title={
              <Stack direction="row" alignItems="center" spacing={1}>
                <ReportIcon fontSize="small" />
                <Typography variant="h6" fontWeight={700}>
                  AI Compliance Review
                </Typography>
              </Stack>
            }
            subheader={
              statusMessage ? (
                <Typography variant="body2" color="text.secondary">
                  {statusMessage}
                </Typography>
              ) : (
                "Review red flags and key analysis from the uploaded document."
              )
            }
            sx={{ pb: 1.5 }}
          />
          <Divider />

          <CardContent sx={{ flex: 1, overflow: "auto", p: { xs: 2, md: 3 } }}>
            {loading ? (
              <Box textAlign="center" mt={8}>
                <CircularProgress />
                <Typography variant="body2" mt={2} color="text.secondary">
                  {statusMessage || "Running analysis..."}
                </Typography>
              </Box>
            ) : aiResponse?.length > 0 ? (
              <Stack spacing={4}>
                {aiResponse.map((result, idx) => (
                  <Paper key={idx} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700 }}>
                      Task ID: {result.task_id}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Task:</strong> {result.task}
                    </Typography>
                    
                    {/* Red Flags */}
                    <Typography variant="subtitle1" mt={2} fontWeight={700} color="error.main">
                      🛑 Red Flags
                    </Typography>
                    {result.ai_response["Red Flags"]?.length ? (
                      <List dense disablePadding>
                        {result.ai_response["Red Flags"].map((flag, i) => (
                          <ListItem key={i}>
                            <ListItemText primary={`• ${flag}`} primaryTypographyProps={{ variant: "body2" }} />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2" color="text.secondary">None found</Typography>
                    )}

                    {/* Analysis */}
                    <Typography variant="subtitle1" mt={2} fontWeight={700}>
                      📋 Analysis
                    </Typography>
                    {result.ai_response["Analysis"]?.length ? (
                      <List dense disablePadding>
                        {result.ai_response["Analysis"].map((point, i) => (
                          <ListItem key={i}>
                            <ListItemText primary={`• ${point}`} primaryTypographyProps={{ variant: "body2" }} />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2" color="text.secondary">None available</Typography>
                    )}
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  height: "100%",
                  p: 3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 2,
                  borderStyle: "dashed",
                  bgcolor: "#fcfdff",
                }}
              >
                <Typography color="text.secondary" align="center">
                  Upload your <b>Compliance Document</b> and <b>Instruction Sheet (Excel)</b> to begin the review.
                </Typography>
              </Paper>
            )}
          </CardContent>

          {/* Progress Indicator (Bottom) */}
          {uploadProgress > 0 && uploadProgress < 100 && (
            <>
              <Divider />
              <Box sx={{ p: 2 }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="caption" display="block" textAlign="center" mt={0.5}>
                  Uploading: {uploadProgress}%
                </Typography>
              </Box>
            </>
          )}
        </Card>
      </Box>


      {/* Right: Upload & Actions */}
      <Box flex={1} minWidth={320}>
        <Card elevation={1} sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <CardHeader
            title="Compliance Agent"
            subheader="Upload Document and Compliance Instructions (Excel), then run analysis."
            sx={{ pb: 1.5 }}
          />
          <Divider />
          <CardContent sx={{ flex: 1, overflowY: "auto", p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
              {/* Selected files display */}
              {(docFile || instrFile) && (
                <Stack spacing={1}>
                  {docFile && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" color="text.secondary" noWrap>
                        Document: {docFile.name}
                      </Typography>
                      <IconButton size="small" onClick={clearDoc} aria-label="clear document">
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  )}
                  {instrFile && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" color="text.secondary" noWrap>
                        Instructions (Excel): {instrFile.name}
                      </Typography>
                      <IconButton size="small" onClick={clearInstr} aria-label="clear instruction">
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  )}
                </Stack>
              )}

              {/* Upload Document */}
              <Button
                variant="outlined"
                fullWidth
                size="medium"
                startIcon={<UploadIcon />}
                onClick={() => docInputRef.current?.click()}
              >
                Upload Document
              </Button>
              <input
                hidden
                ref={docInputRef}
                type="file"
                accept=".pdf,.xls,.xlsx,.csv"
                onChange={onDocChange}
              />

              {/* Upload Compliance Instruction (Excel) */}
              <Button
                variant="outlined"
                fullWidth
                size="medium"
                startIcon={<ExcelIcon />}
                onClick={() => instrInputRef.current?.click()}
              >
                Upload Compliance Instruction (Excel)
              </Button>
              <input
                hidden
                ref={instrInputRef}
                type="file"
                accept=".xls,.xlsx"
                onChange={onInstrChange}
              />

              {/* Submit */}
              <Button
                variant="contained"
                size="medium"
                onClick={handleSubmit}
                disabled={!canSubmit}
                sx={{ mt: 0.5 }}
              >
                Submit & Analyze
              </Button>

              {/* Actions */}
              <Divider sx={{ my: 1.5 }} />
              <Stack spacing={1.2}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<PdfIcon />}
                  onClick={handleDownloadPdf}
                  disabled={!aiResponse}
                >
                  PDF Download
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  fullWidth
                  startIcon={<ExcelIcon />}
                  onClick={handleUpdateExcel}
                  disabled={!aiResponse}
                >
                  Update in Excel
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {/* ===== Embedded Excel Dialog ===== */}
      <Dialog
        open={excelOpen}
        onClose={() => setExcelOpen(false)}
        fullWidth
        maxWidth="xl"
        PaperProps={{ sx: { height: "90vh" } }}
      >
        <DialogTitle>Excel Editor</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {baseSharepointUrl ? (
            <iframe
              key={iframeKey}
              title="Embedded Excel"
              width="100%"
              height="100%"
              frameBorder="0"
              scrolling="no"
              src={`${baseSharepointUrl}&timestamp=${iframeKey}`}
              style={{ display: "block", minHeight: "75vh" }}
            />
          ) : (
            <Box sx={{ p: 2 }}>
              <Alert severity="warning">
                Excel link is not configured. Please set a valid Compliance Excel URL.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIframeKey(Date.now())}>Refresh</Button>
          <Button onClick={() => setExcelOpen(false)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4200}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="error" onClose={() => setSnackbarOpen(false)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}
